/** @jsxImportSource @emotion/react */
import {
  Autocomplete,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { css } from "@emotion/react";
import { Box } from "@mui/system";
import styled from "@emotion/styled";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Markdown from "markdown-to-jsx";
import {
  getNewsListDetails,
  getNewsTagDetails,
  getNewsUpdateDetails,
  queryUpdateDetails,
} from "./newsUpdateApiRequest";
import { v4 } from "uuid";
import moment from "moment";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinearBufferSpinner from "../../shared/components/spinners/LinearBufferSpinner";
import { MOBLIE_VIEW_WIDTH } from "../../shared/constants/constants";

const NewsWrapper = styled(Box)(({ mobileMatches }) => ({
  backgroundColor: "#121212",
  minHeight: "61vh",
  padding: "0 4%",
  ".topBar": {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: mobileMatches ? "3% 0" : "7% 0",

    ".MuiOutlinedInput-input": {
      height: "1.5rem",
    },
    ".MuiFormLabel-root": {
      bottom: "1.5rem",
      top: "unset",
    },
    ".MuiOutlinedInput-root": {
      padding: "unset",
      color: "white",
    },
    ".MuiAutocomplete-listbox": {
      color: "black",
    },
    ".css-10y3i1v-MuiFormLabel-root-MuiInputLabel-root": {
      color: "rgb(118, 118, 118)",
    },
    ".css-wntiqe-MuiFormLabel-root-MuiInputLabel-root": {
      color: "#007aff",
    },
    button: {
      borderRadius: "unset",
      color: "white",
    },
    ".MuiCardActionArea-root": {
      "& > p": {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      },
    },
  },
}));

const cardCss = css`
  margin-bottom: 2%;
  background: black;
  color: white;
  p {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .MuiTypography-h6 {
    font-size: 1.5rem;
    line-height: 1.334;
  }
  #subTitlePipeId {
    :before {
      content: "|";
      margin: 0px 8px;
    }
  }
`;
const descriptionWrapper = css`
  font-size: 14px;
  line-height: 35px;
`;
const fontFamilyWrapper = (theme) => css`
  font-family: ${theme.typography.MBCORPOA};
`;
const cardWrapperCss = (mobileMatches) => css`
  display: grid;
  grid-template-columns: ${mobileMatches ? "repeat(3, 1fr)" : "1fr"};
  grid-template-rows: auto auto auto;
  row-gap: 12px;
  column-gap: 16px;
  min-height: 21vh;
`;

const NewsUpdate = () => {
  const [newsData, setNewsData] = useState([]);
  const [newsTags, setNewsTags] = useState();
  const [pageSize, setPageSize] = useState(1);
  const [filter, setFilter] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [loader, setLoader] = useState(false);
  const [newsListingData, setNewsListingData] = useState();

  const mobileMatches = useMediaQuery(MOBLIE_VIEW_WIDTH);
  const navigate = useNavigate();

  useEffect(() => {
    getNewsListDetails().then(({ data }) => {
      document.title = data?.data?.attributes?.title;
      const newsListingDataResponse = data.data.attributes.body[0];
      setNewsListingData(newsListingDataResponse);
    });
  }, []);

  const top3Filteration = useCallback(
    (tag, data, newsTagData) => {
      if (newsListingData?.allFilterOption === tag) {
        return setNewsData(data.data);
      }

      let top3 = (newsTags || newsTagData).filter(
        (data) => data?.attributes?.title === tag
      );
      let topNews1, topNews2, topNews3;
      if (top3[0].attributes.body[0]) {
        topNews1 = top3[0].attributes.body[0].topNews1;
        topNews2 = top3[0].attributes.body[0].topNews2;
        topNews3 = top3[0].attributes.body[0].topNews3;
      }
      const top3News = [
        topNews1?.data?.attributes?.title,
        topNews2?.data?.attributes?.title,
        topNews3?.data?.attributes?.title,
      ];

      if (top3[0].attributes.body.length > 0) {
        const filteredData = data.data.filter(
          (d) => !top3News.includes(d.attributes.title)
        );
        let filteredDataToBeAdded = [];

        data.data.forEach((d) => {
          switch (d?.attributes?.title) {
            case topNews1?.data?.attributes?.title:
              filteredDataToBeAdded[0] = d;
              break;
            case topNews2?.data?.attributes?.title:
              filteredDataToBeAdded[1] = d;
              break;
            case topNews3?.data?.attributes?.title:
              filteredDataToBeAdded[2] = d;
              break;
            default:
              break;
          }
        });
        filteredDataToBeAdded = filteredDataToBeAdded.filter(
          (d) => d !== undefined
        );

        setNewsData([...filteredDataToBeAdded, ...filteredData]);
      } else {
        setNewsData(data.data);
      }
    },
    [newsTags]
  );

  useEffect(() => {
    newsListingData &&
      getNewsTagDetails().then(({ data }) => {
        const newsTagData = data.data;
        newsTagData.unshift({
          attributes: { title: newsListingData?.allFilterOption },
        });
        setNewsTags(newsTagData);

        const trendingTag =
          newsListingData?.trendingTag?.data?.attributes?.title;
        setFilter(trendingTag || newsListingData?.allFilterOption);
        setLoader(true);
        trendingTag
          ? queryUpdateDetails(trendingTag, 1)
              .then(({ data }) => {
                top3Filteration(trendingTag, data, newsTagData);
                setPageCount(data.meta.pagination.pageCount);
                setLoader(false);
              })
              .catch((err) => {
                console.log("Error::", err.message);
                setLoader(false);
              })
          : getNewsUpdateDetails(1)
              .then(({ data }) => {
                top3Filteration(trendingTag, data, newsTagData);
                setPageCount(data.meta.pagination.pageCount);
                setLoader(false);
              })
              .catch((err) => {
                console.log("Error::", err.message);
                setLoader(false);
              });
      });
  }, [newsListingData]);

  const onChangeFunc = ({ target }) => {
    setPageSize(1);
    setFilter(target.innerText);
    setLoader(true);
    if (target.innerText === newsListingData?.allFilterOption) {
      getNewsUpdateDetails(1)
        .then(({ data }) => {
          top3Filteration(target.innerText, data);
          setPageCount(data.meta.pagination.pageCount);
        })
        .catch((err) => {
          console.log("Error::", err.message);
        });
    } else {
      queryUpdateDetails(target.innerText, 1)
        .then(({ data }) => {
          top3Filteration(target.innerText, data);
          setPageCount(data.meta.pagination.pageCount);
        })
        .catch((err) => {
          console.log("Error::", err.message);
        });
    }
    setLoader(false);
  };

  const loadMoreClick = () => {
    setLoader(true);
    setPageSize(pageSize + 1);
    if (filter != newsListingData?.allFilterOption) {
      queryUpdateDetails(filter, pageSize + 1).then(({ data }) => {
        setNewsData([...newsData, ...data.data]);
        setPageCount(data.meta.pagination.pageCount);
      });
    } else {
      getNewsUpdateDetails(pageSize + 1).then(({ data }) => {
        const updatedNewData = data.data;
        setNewsData([...newsData, ...updatedNewData]);
        setPageCount(data.meta.pagination.pageCount);
      });
    }
    setLoader(false);
  };

  return (
    <NewsWrapper mobileMatches={mobileMatches}>
      <Box className="topBar">
        <Typography
          variant="h5"
          sx={{
            color: "white",
          }}
        >
          {newsListingData?.title}
        </Typography>
        {filter && (
          <Autocomplete
            disablePortal
            disableClearable
            id="combo-box-demo"
            getOptionLabel={(option) => option.attributes.title}
            options={newsTags}
            sx={{ width: mobileMatches ? 200 : 150 }}
            onChange={onChangeFunc}
            value={{ attributes: { title: filter } }}
            renderInput={(params) => (
              <TextField {...params} label={newsListingData?.filterLabel} />
            )}
          />
        )}
      </Box>
      <Box css={() => cardWrapperCss(mobileMatches)}>
        {newsData &&
          newsData.map(({ attributes }) => (
            <Card css={cardCss} key={v4()}>
              <CardActionArea
                onClick={() =>
                  navigate(`/news/news-update/news-detail/${attributes.slug}`)
                }
              >
                <CardMedia
                  component="img"
                  height="240px"
                  image={`${process.env.REACT_APP_BASEURL}${attributes?.thumbnailImage?.data?.attributes?.url}`}
                  alt={`${process.env.REACT_APP_BASEURL}${attributes?.thumbnailImage?.data?.attributes?.name}`}
                />
                <CardContent>
                  <Typography
                    gutterBottom
                    variant="h6"
                    component="div"
                    display="inline"
                    css={fontFamilyWrapper}
                  >
                    {attributes?.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      display: attributes?.subTitle ? "inline" : "none",
                      fontFamily: (theme) => theme.typography.MBCORPOA,
                    }}
                    id="subTitlePipeId"
                  >
                    {attributes?.subTitle}
                  </Typography>
                  <Typography variant="body2" color="#949494">
                    {moment(attributes?.updatedAt).format("DD-MM-YYYY HH:mm")}
                  </Typography>
                  <Typography variant="body2">
                    <Markdown css={descriptionWrapper}>
                      {attributes?.description || ""}
                    </Markdown>
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
      </Box>

      {pageSize !== pageCount && (
        <Box
          component="span"
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          {newsData.length > 0 && !loader ? (
            <Button
              sx={{ textDecoration: "underline", fontSize: "14px" }}
              onClick={loadMoreClick}
            >
              {newsListingData.loadMore}
              <KeyboardArrowDownIcon
                sx={{ color: "#1976d2", p: 1 }}
                id="rock"
              />
            </Button>
          ) : loader ? (
            <LinearBufferSpinner />
          ) : (
            <Typography variant="h5" color="text.secondary">
              {newsListingData?.noDataFound}
            </Typography>
          )}
        </Box>
      )}
    </NewsWrapper>
  );
};

export default NewsUpdate;
